import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeroOfferStack } from "../lib/checkout";
import { MaskedLine } from "../lib/anim";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-[#050505]">
      <motion.div
        style={{ opacity: glowOpacity }}
        className="pointer-events-none absolute -top-40 right-[-15%] h-[34rem] w-[34rem] rounded-full"
      >
        <div className="h-full w-full rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.16) 0%, rgba(255,0,127,0.05) 45%, transparent 70%)" }} />
      </motion.div>

      <div className="mx-auto grid max-w-7xl gap-10 px-6 pb-24 pt-14 sm:pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-6 lg:pb-32 lg:pt-24">
        <div className="relative z-10">
          <MaskedLine delay={0.15}>
            <span className="text-[11px] uppercase tracking-[0.4em] gold-text ff-sans font-medium">
              Pleasure • Presence • Connection
            </span>
          </MaskedLine>

          <h1 className="mt-6 ff-serif font-light leading-[0.92] tracking-tight text-[#fdfbf7]">
            <MaskedLine delay={0.3}>
              <span className="block text-[17vw] sm:text-7xl lg:text-8xl">PLEASURE</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="block text-[17vw] sm:text-7xl lg:text-8xl gold-text italic">PORTAL™</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.65} className="mt-6">
            <span className="ff-script text-3xl sm:text-4xl text-[#f7e7ce]">come back to your body.</span>
          </MaskedLine>

          <MaskedLine delay={0.8} className="mt-6 max-w-md">
            <span className="block text-sm sm:text-base font-light leading-relaxed tracking-wide text-[#f7e7ce]/75 ff-sans">
              A botanical body ritual created for intentional moments of touch, sensation, presence and
              connection.
            </span>
          </MaskedLine>

          <MaskedLine delay={0.88} className="mt-4">
            <span className="block text-[11px] uppercase tracking-[0.3em] text-[#f7e7ce]/50 ff-sans leading-loose">
              For women. For men. For couples. <span className="gold-text">For you.</span>
            </span>
          </MaskedLine>

          <div className="mt-10 lg:hidden">
            <motion.div style={{ y: artY }} className="relative mx-auto w-full max-w-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="glow-soft overflow-hidden rounded-t-[999px] border hairline-gold"
              >
                <img
                  src="/images/bottle.jpg"
                  alt="Pleasure Portal Spray — golden root botanical body spray"
                  className="w-full object-cover"
                  data-testid="hero-artwork-mobile"
                />
              </motion.div>
            </motion.div>
          </div>

          <div className="mt-10 max-w-md">
            <HeroOfferStack />
          </div>
        </div>

        <motion.div style={{ y: artY }} className="relative hidden lg:block">
          <div className="pointer-events-none absolute inset-0 -z-0" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.2), transparent 65%)" }} />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="glow-soft mx-auto max-w-[460px] overflow-hidden rounded-t-[999px] border hairline-gold"
          >
            <img
              src="/images/bottle.jpg"
              alt="Pleasure Portal Spray — golden root botanical body spray"
              className="w-full object-cover"
              data-testid="hero-artwork"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
